import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  LayoutDashboard, ListChecks, Bot, History, Mail, Store, Check, X,
  Undo2, Send, Star, AlertTriangle, RefreshCw, ShieldCheck, Sparkles, CalendarClock, LogOut, Eye, Trash2,
} from "lucide-react";
import { api, mediaUrl } from "../lib/api";
import { ConfidenceBar, RiskChip } from "../components/ConfidenceBar";
import { Logo } from "../components/Layout";
import { useAuth } from "../context/AuthContext";

const TABS = [
  { key: "overview", label: "Resumen", icon: LayoutDashboard },
  { key: "queue", label: "Cola de revisión", icon: ListChecks },
  { key: "agents", label: "Agentes", icon: Bot },
  { key: "audit", label: "Auditoría", icon: History },
  { key: "newsletter", label: "Newsletter", icon: Mail },
  { key: "businesses", label: "Negocios", icon: Store },
];

export default function Admin() {
  const [tab, setTab] = useState("overview");
  const { user, logout } = useAuth();
  return (
    <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-brand-ink text-white"><ShieldCheck className="h-5 w-5" /></span>
          <div>
            <h1 className="font-display text-2xl font-extrabold tracking-tight">Panel de control</h1>
            <p className="text-sm text-muted-foreground">Supervisión, colas y auditoría — <Logo className="text-sm" /></p>
          </div>
        </div>
        <button data-testid="logout-btn" onClick={logout} className="inline-flex items-center gap-1.5 rounded-full border border-border px-4 py-2 text-sm font-semibold text-brand-ink transition-colors hover:bg-secondary">
          <LogOut className="h-4 w-4" /> <span className="hidden sm:inline">Salir</span>
        </button>
      </div>
      {user && <p className="mt-1 text-xs text-muted-foreground">Sesión: {user.email}</p>}

      <div className="ml-scroll mt-5 flex gap-1.5 overflow-x-auto border-b border-border pb-px">
        {TABS.map((t) => {
          const Icon = t.icon;
          return (
            <button key={t.key} data-testid={`admin-tab-${t.key}`} onClick={() => setTab(t.key)}
              className={`inline-flex shrink-0 items-center gap-1.5 rounded-t-xl px-4 py-2.5 text-sm font-semibold transition-colors ${tab === t.key ? "bg-brand-ink text-white" : "text-brand-ink hover:bg-secondary"}`}>
              <Icon className="h-4 w-4" /> {t.label}
            </button>
          );
        })}
      </div>

      <div className="mt-6">
        {tab === "overview" && <Overview />}
        {tab === "queue" && <Queue />}
        {tab === "agents" && <Agents />}
        {tab === "audit" && <Audit />}
        {tab === "newsletter" && <NewsletterAdmin />}
        {tab === "businesses" && <Businesses />}
      </div>
    </div>
  );
}

/* ---------------- Overview ---------------- */
function Overview() {
  const [m, setM] = useState(null);
  useEffect(() => { api.adminMetrics().then(setM).catch(() => {}); }, []);
  if (!m) return <Skeleton />;
  const cards = [
    { l: "Revisiones pendientes", v: m.pending_reviews, c: "text-amber-600" },
    { l: "Negocios publicados", v: m.published },
    { l: "Fichas reclamadas", v: m.claimed },
    { l: "Destacados", v: m.featured, c: "text-brand" },
    { l: "Lugares", v: m.places },
    { l: "Suscriptores", v: m.subscribers },
    { l: "Vistas", v: m.views },
    { l: "Clics de contacto", v: m.clicks },
  ];
  return (
    <div>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {cards.map((c) => (
          <div key={c.l} data-testid={`metric-${c.l}`} className="rounded-2xl border border-border bg-card p-4">
            <p className={`font-display text-3xl font-extrabold tracking-tight ${c.c || "text-brand-ink"}`}>{c.v}</p>
            <p className="mt-1 text-xs font-medium text-muted-foreground">{c.l}</p>
          </div>
        ))}
      </div>
      <div className="mt-5 rounded-2xl border border-border bg-card p-5">
        <h3 className="font-display text-lg font-bold">Cola por nivel de riesgo</h3>
        <div className="mt-3 flex flex-wrap gap-3">
          {Object.entries(m.pending_by_risk || {}).length === 0 && <p className="text-sm text-muted-foreground">Sin pendientes.</p>}
          {Object.entries(m.pending_by_risk || {}).map(([r, n]) => (
            <div key={r} className="flex items-center gap-2 rounded-xl bg-secondary/60 px-4 py-2"><RiskChip risk={r} /><span className="font-bold">{n}</span></div>
          ))}
        </div>
        <p className="mt-4 flex items-center gap-1.5 text-xs text-muted-foreground"><AlertTriangle className="h-3.5 w-3.5" /> Merges, newsletters y cambios sensibles siempre requieren tu aprobación.</p>
      </div>
    </div>
  );
}

/* ---------------- Review Queue ---------------- */
function Queue() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    api.reviewQueue(filter ? { type: filter } : {}).then((d) => { setItems(d); setLoading(false); }).catch(() => setLoading(false));
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  const act = async (id, approve) => {
    try {
      if (approve) { await api.approve(id); toast.success("Aprobado y publicado."); }
      else { await api.reject(id, "Rechazado por revisión manual"); toast.success("Rechazado."); }
      setItems((p) => p.filter((x) => x.id !== id));
    } catch { toast.error("Acción fallida."); }
  };

  const TYPES = [["", "Todos"], ["business_create", "Fichas nuevas"], ["business_update", "Actualizaciones"], ["merge", "Duplicados"], ["newsletter", "Newsletter"], ["place_create", "Lugares"]];

  return (
    <div>
      <div className="ml-scroll flex gap-2 overflow-x-auto pb-1">
        {TYPES.map(([v, l]) => (
          <button key={v} data-testid={`queue-filter-${v || "all"}`} onClick={() => setFilter(v)} className={`shrink-0 rounded-full border px-3.5 py-1.5 text-sm font-semibold ${filter === v ? "border-brand bg-brand text-white" : "border-border bg-white hover:border-brand/40"}`}>{l}</button>
        ))}
      </div>
      {loading ? <Skeleton /> : items.length === 0 ? (
        <div className="mt-10 flex flex-col items-center text-center text-muted-foreground"><Check className="h-10 w-10 text-emerald-500" /><p className="mt-2 font-semibold">Cola vacía. ¡Todo revisado!</p></div>
      ) : (
        <div className="mt-4 space-y-3">
          {items.map((it) => (
            <div key={it.id} data-testid={`queue-item-${it.id}`} className="rounded-2xl border border-border bg-card p-4">
              <div className="flex flex-wrap items-center gap-2">
                <span className="rounded-md bg-secondary px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide text-brand-ink">{it.agent_name}</span>
                <RiskChip risk={it.risk} />
                <ConfidenceBar score={it.confidence} size="sm" />
              </div>
              <div className="mt-2 flex gap-3">
                {it.payload?.image_url && (
                  <img src={mediaUrl(it.payload.image_url)} alt={it.title} loading="lazy" className="h-16 w-16 shrink-0 rounded-xl object-cover" />
                )}
                <div className="min-w-0">
                  <h4 className="font-display text-lg font-bold leading-tight text-brand-ink">{it.title}</h4>
                  {it.payload?.phone && <p className="text-xs text-muted-foreground">{it.payload.phone}{it.payload.address ? ` · ${it.payload.address}` : ""}</p>}
                </div>
              </div>
              {it.reasons?.length > 0 && (
                <ul className="mt-1.5 space-y-0.5">
                  {it.reasons.map((r, i) => <li key={i} className="text-xs text-muted-foreground">• {r}</li>)}
                </ul>
              )}
              <div className="mt-3 flex gap-2">
                <button data-testid={`approve-${it.id}`} onClick={() => act(it.id, true)} className="inline-flex items-center gap-1.5 rounded-full bg-emerald-600 px-4 py-2 text-sm font-bold text-white hover:bg-emerald-700"><Check className="h-4 w-4" /> Aprobar</button>
                <button data-testid={`reject-${it.id}`} onClick={() => act(it.id, false)} className="inline-flex items-center gap-1.5 rounded-full border border-rose-200 px-4 py-2 text-sm font-bold text-rose-600 hover:bg-rose-50"><X className="h-4 w-4" /> Rechazar</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------------- Agents console ---------------- */
const AGENT_CFG = {
  discovery: { desc: "Descubre nuevos negocios por mercado y categoría", fields: ["market", "category"], note: true, placeholder: "Ej: enfócate en food trucks y negocios abiertos de noche" },
  enrichment: { desc: "Completa campos faltantes (auto-publica si confianza ≥ 85%)", fields: ["business"], note: false },
  deduplication: { desc: "Detecta duplicados y propone merges reversibles", fields: [], note: false },
  newsletter: { desc: "Redacta borradores de newsletter en español", fields: [], note: true, placeholder: "Ej: tono festivo, destaca ofertas de fin de semana" },
  places: { desc: "Propone lugares para visitar", fields: ["city"], note: true, placeholder: "Ej: enfócate en eventos familiares y gratuitos" },
  safety: { desc: "Revisa riesgo y envía lo sensible a la cola", fields: [], note: false },
};

function Agents() {
  const [agents, setAgents] = useState([]);
  const [sel, setSel] = useState("discovery");
  const [conv, setConv] = useState(null);
  const [activity, setActivity] = useState(null);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [executing, setExecuting] = useState(false);

  useEffect(() => { api.agents().then(setAgents).catch(() => {}); }, []);

  const loadConv = (key) => api.agentChat(key).then(setConv).catch(() => setConv({ messages: [], pending_plan: null }));
  const loadActivity = (key) => api.agentActivity(key).then(setActivity).catch(() => setActivity(null));
  useEffect(() => { setConv(null); setActivity(null); loadConv(sel); loadActivity(sel); }, [sel]);

  const cfg = AGENT_CFG[sel];
  const current = agents.find((a) => a.key === sel);
  const messages = conv?.messages || [];
  const plan = conv?.pending_plan;

  const send = async () => {
    const msg = input.trim();
    if (!msg || sending) return;
    setSending(true);
    setConv((c) => ({ ...(c || {}), messages: [...(c?.messages || []), { role: "user", text: msg, at: new Date().toISOString() }], pending_plan: c?.pending_plan }));
    setInput("");
    try {
      const r = await api.sendAgentChat(sel, msg);
      setConv(r);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "El agente no pudo responder.");
    } finally { setSending(false); }
  };

  const execute = async () => {
    setExecuting(true);
    try {
      const r = await api.executeAgentPlan(sel);
      setConv(r.conversation);
      toast.success(r.message || "Plan ejecutado.");
      loadActivity(sel);
      api.agents().then(setAgents).catch(() => {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo ejecutar el plan.");
    } finally { setExecuting(false); }
  };

  const resetChat = async () => {
    try { const r = await api.resetAgentChat(sel); setConv(r); toast.success("Conversación reiniciada."); }
    catch { toast.error("No se pudo reiniciar."); }
  };

  const actDraft = async (id, approve) => {
    try {
      if (approve) { await api.approve(id); toast.success("Publicado en el sitio ✓"); }
      else { await api.reject(id, "Rechazado desde la consola"); toast.success("Borrador rechazado"); }
      loadActivity(sel);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo completar la acción.");
    }
  };

  const fmt = (t) => { try { return new Date(t).toLocaleString("es", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); } catch { return ""; } };

  const planChips = plan ? [
    ["Mercado", plan.market], ["Ciudad", plan.city], ["Categoría", plan.category], ["Negocio", plan.business_id],
  ].filter(([, v]) => v) : [];

  return (
    <div className="space-y-4">
      <div className="flex items-start gap-3 rounded-2xl border border-brand/20 bg-brand/5 p-4" data-testid="agents-schedule-banner">
        <CalendarClock className="mt-0.5 h-5 w-5 shrink-0 text-brand" />
        <div>
          <p className="text-sm font-bold text-brand-ink">Habla con tus agentes y acuerden el plan</p>
          <p className="text-xs text-muted-foreground">Escríbele a cada agente lo que quieres. Él repetirá lo que entendió y te propondrá un plan; <strong>nada se ejecuta hasta que lo apruebas</strong>. Lo que generen llega a la cola de revisión para tu aprobación final. Además, cada día (13:00 UTC) trabajan solos sobre tus mercados semilla.</p>
        </div>
      </div>
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-[260px_minmax(0,1fr)]">
      {/* Agent roster */}
      <div className="ml-scroll flex min-w-0 gap-2 overflow-x-auto lg:flex-col lg:overflow-visible">
        {agents.map((a) => (
          <button
            key={a.key}
            data-testid={`agent-select-${a.key}`}
            onClick={() => setSel(a.key)}
            className={`flex shrink-0 items-center gap-2 rounded-2xl border px-4 py-3 text-left transition-all lg:w-full ${sel === a.key ? "border-brand bg-brand text-white shadow-md" : "border-border bg-card hover:border-brand/40"}`}
          >
            <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${sel === a.key ? "bg-white/20" : "bg-brand/10 text-brand"}`}><Bot className="h-5 w-5" /></span>
            <span>
              <span className="block text-sm font-bold leading-tight">{a.name}</span>
              <span className={`block text-[11px] ${sel === a.key ? "text-white/70" : "text-muted-foreground"}`}>
                {a.last_run ? `Activo · ${a.last_run.items ?? 0} items` : "Inactivo"}
              </span>
            </span>
          </button>
        ))}
      </div>

      {/* Chat console */}
      <div className="flex min-w-0 flex-col rounded-2xl border border-border bg-card" data-testid={`agent-console-${sel}`}>
        <div className="flex items-center justify-between gap-2 border-b border-border p-5">
          <div className="flex items-center gap-2">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-brand/10 text-brand"><Bot className="h-6 w-6" /></span>
            <div>
              <h3 className="font-display text-lg font-bold text-brand-ink">{current?.name || "Agente"}</h3>
              <p className="text-xs text-muted-foreground">{cfg.desc}</p>
            </div>
          </div>
          {messages.length > 0 && (
            <button data-testid="reset-chat-btn" onClick={resetChat} className="inline-flex items-center gap-1 rounded-full border border-border px-3 py-1.5 text-xs font-semibold text-muted-foreground hover:bg-secondary">
              <RefreshCw className="h-3.5 w-3.5" /> Reiniciar
            </button>
          )}
        </div>

        {/* Conversation thread */}
        <div className="ml-scroll flex max-h-[420px] flex-col gap-4 overflow-y-auto p-5" data-testid="agent-chat-thread">
          {messages.length === 0 && (
            <Bubble name={current?.name} time="ahora">
              ¡Hola! Soy {current?.name || "tu agente"}. Cuéntame qué quieres que haga y te propondré un plan antes de ejecutarlo.
            </Bubble>
          )}
          {messages.map((m, i) => (
            m.role === "user" ? (
              <div key={i} className="flex justify-end">
                <div data-testid={`chat-user-${i}`} className="max-w-[80%] rounded-2xl rounded-tr-sm bg-brand px-3.5 py-2.5 text-sm font-medium text-white">{m.text}</div>
              </div>
            ) : (
              <Bubble key={i} name={current?.name} time={fmt(m.at)} error={m.error}>{m.text}</Bubble>
            )
          ))}
          {sending && (
            <Bubble name={current?.name} time="ahora">
              <span className="inline-flex items-center gap-2 text-muted-foreground"><RefreshCw className="h-3.5 w-3.5 animate-spin" /> Pensando...</span>
            </Bubble>
          )}

          {/* Agreed plan awaiting approval */}
          {plan && !sending && (
            <div data-testid="agent-plan-card" className="rounded-2xl border-2 border-brand/30 bg-brand/5 p-4">
              <p className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-brand"><Sparkles className="h-3.5 w-3.5" /> Plan propuesto</p>
              {plan.summary && <p className="mt-1.5 text-sm font-semibold text-brand-ink">{plan.summary}</p>}
              {planChips.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {planChips.map(([l, v]) => (
                    <span key={l} className="rounded-full bg-white px-2.5 py-0.5 text-[11px] font-semibold text-brand-ink">{l}: {v}</span>
                  ))}
                </div>
              )}
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <button data-testid="approve-plan-btn" disabled={executing} onClick={execute} className="inline-flex items-center gap-1.5 rounded-full bg-emerald-600 px-4 py-2 text-sm font-bold text-white hover:bg-emerald-700 disabled:opacity-60">
                  {executing ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                  {executing ? "Ejecutando..." : "Aprobar y ejecutar"}
                </button>
                <span className="text-xs text-muted-foreground">o escribe abajo para ajustarlo</span>
              </div>
            </div>
          )}
        </div>

        {/* Input */}
        <div className="border-t border-border p-4">
          <div className="flex items-end gap-2">
            <textarea
              data-testid="agent-chat-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              rows={2}
              placeholder={cfg.placeholder || "Escríbele al agente qué quieres que haga..."}
              className="min-w-0 flex-1 resize-none rounded-xl border border-input bg-white px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-brand/40"
            />
            <button data-testid="agent-chat-send" disabled={sending || !input.trim()} onClick={send} className="inline-flex h-11 shrink-0 items-center gap-1.5 rounded-full bg-brand-ink px-4 text-sm font-bold text-white hover:opacity-90 disabled:opacity-50">
              <Send className="h-4 w-4" /> <span className="hidden sm:inline">Enviar</span>
            </button>
          </div>
        </div>
      </div>
      </div>

      {/* Generated results — approve to publish */}
      {activity?.produced?.length > 0 && (
        <div className="rounded-2xl border border-border bg-card p-5" data-testid="agent-results">
          <p className="mb-2 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-muted-foreground"><Sparkles className="h-3.5 w-3.5" /> Resultados generados — apruébalos para publicarlos</p>
          <div className="space-y-2">
            {activity.produced.slice(0, 12).map((p) => (
              <div key={p.id} data-testid={`produced-${p.id}`} className="flex flex-wrap items-center justify-between gap-2 rounded-xl bg-secondary/50 px-3 py-2">
                <div className="flex min-w-0 items-center gap-2">
                  {p.payload?.image_url && <img src={mediaUrl(p.payload.image_url)} alt="" loading="lazy" className="h-9 w-9 shrink-0 rounded-lg object-cover" />}
                  <span className="truncate text-sm font-medium text-brand-ink">{p.title}</span>
                </div>
                <span className="flex shrink-0 items-center gap-2">
                  <ConfidenceBar score={p.confidence} size="sm" />
                  {p.status === "pending" ? (
                    <>
                      <button data-testid={`produced-approve-${p.id}`} onClick={() => actDraft(p.id, true)} className="inline-flex items-center gap-1 rounded-full bg-emerald-600 px-3 py-1 text-xs font-bold text-white hover:bg-emerald-700"><Check className="h-3.5 w-3.5" /> Publicar</button>
                      <button data-testid={`produced-reject-${p.id}`} onClick={() => actDraft(p.id, false)} className="inline-flex items-center gap-1 rounded-full border border-rose-200 px-2.5 py-1 text-xs font-bold text-rose-600 hover:bg-rose-50"><X className="h-3.5 w-3.5" /></button>
                    </>
                  ) : (
                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${p.status === "approved" ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                      {p.status === "approved" ? "Publicado" : "Rechazado"}
                    </span>
                  )}
                </span>
              </div>
            ))}
          </div>
          <p className="mt-2 text-[11px] text-muted-foreground">Pulsa “Publicar” para que aparezca en el directorio, o gestiónalos en la pestaña “Cola de revisión”.</p>
        </div>
      )}
    </div>
  );
}

const Bubble = ({ name, time, children, error }) => (
  <div className="flex gap-2.5">
    <span className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${error ? "bg-rose-100 text-rose-600" : "bg-brand/10 text-brand"}`}><Bot className="h-4 w-4" /></span>
    <div>
      <p className="text-xs font-bold text-brand-ink">{name} <span className="ml-1 font-normal text-muted-foreground">· {time}</span></p>
      <div className={`mt-1 rounded-2xl rounded-tl-sm px-3.5 py-2.5 text-sm ${error ? "bg-rose-50 text-rose-700" : "bg-secondary/70 text-brand-ink"}`}>{children}</div>
    </div>
  </div>
);

/* ---------------- Audit ---------------- */
function Audit() {
  const [items, setItems] = useState([]);
  const load = () => api.audit().then(setItems).catch(() => {});
  useEffect(() => { load(); }, []);
  const revert = async (id) => {
    try { await api.revert(id); toast.success("Cambio revertido."); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "No se pudo revertir."); }
  };
  return (
    <div className="overflow-hidden rounded-2xl border border-border bg-card">
      <div className="ml-scroll overflow-x-auto">
        <table className="w-full min-w-[640px] text-sm">
          <thead className="bg-secondary/60 text-left text-xs uppercase tracking-wide text-muted-foreground">
            <tr><th className="p-3">Acción</th><th className="p-3">Entidad</th><th className="p-3">Actor</th><th className="p-3">Nota</th><th className="p-3">Reversible</th></tr>
          </thead>
          <tbody>
            {items.map((a) => (
              <tr key={a.id} data-testid={`audit-${a.id}`} className="border-t border-border">
                <td className="p-3 font-mono text-xs font-semibold text-brand">{a.action}</td>
                <td className="p-3 text-muted-foreground">{a.entity_type}</td>
                <td className="p-3">{a.actor}</td>
                <td className="p-3 text-muted-foreground">{a.note}</td>
                <td className="p-3">
                  {a.reversible && !a.reverted ? (
                    <button data-testid={`revert-${a.id}`} onClick={() => revert(a.id)} className="inline-flex items-center gap-1 rounded-full border border-border px-3 py-1 text-xs font-semibold hover:bg-secondary"><Undo2 className="h-3.5 w-3.5" /> Revertir</button>
                  ) : a.reverted ? <span className="text-xs text-muted-foreground">Revertido</span> : <span className="text-xs text-muted-foreground">—</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ---------------- Newsletter admin ---------------- */
function NewsletterAdmin() {
  const [issues, setIssues] = useState([]);
  const nav = useNavigate();
  const load = () => api.adminIssues().then(setIssues).catch(() => {});
  useEffect(() => { load(); }, []);
  const remove = async (it) => {
    if (!window.confirm(`¿Eliminar la edición "${it.subject}"? Esta acción no se puede deshacer.`)) return;
    try { await api.deleteIssue(it.id); toast.success("Edición eliminada."); load(); }
    catch { toast.error("No se pudo eliminar."); }
  };
  return (
    <div>
      <p className="text-sm text-muted-foreground">Aprueba un borrador en la cola para que aparezca aquí. Abre “Ver y revisar” para verlo completo, pedirle cambios al agente y aprobarlo.</p>
      {issues.length === 0 ? (
        <div className="mt-6 rounded-2xl border border-dashed border-border p-8 text-center text-muted-foreground">Aún no hay ediciones. Ejecuta el agente de Newsletter y apruébalo en la cola.</div>
      ) : (
        <div className="mt-4 space-y-3">
          {issues.map((it) => (
            <div key={it.id} data-testid={`admin-issue-${it.id}`} className="rounded-2xl border border-border bg-card p-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <h4 className="font-display text-lg font-bold text-brand-ink">{it.subject}</h4>
                  <p className="text-sm text-muted-foreground">{it.preview_text}</p>
                </div>
                <span className={`rounded-full px-2.5 py-0.5 text-xs font-bold ${it.status === "sent" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>{it.status === "sent" ? "Enviado" : "Listo"}</span>
              </div>
              {it.status === "sent" && (
                <p className="mt-2 text-xs text-muted-foreground">Destinatarios: {it.recipients} · Aperturas: {it.opens} · Clics: {it.clicks}</p>
              )}
              <div className="mt-3 flex flex-wrap gap-2">
                <button data-testid={`review-issue-${it.id}`} onClick={() => nav(`/admin/newsletter/${it.id}`)} className="inline-flex items-center gap-1.5 rounded-full bg-brand px-4 py-2 text-sm font-bold text-white hover:bg-brand-hover"><Eye className="h-4 w-4" /> Ver y revisar</button>
                <button data-testid={`delete-issue-${it.id}`} onClick={() => remove(it)} className="inline-flex items-center gap-1.5 rounded-full border border-red-200 bg-red-50 px-4 py-2 text-sm font-bold text-red-700 hover:bg-red-100"><Trash2 className="h-4 w-4" /> Eliminar</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------------- Businesses ---------------- */
function Businesses() {
  const [items, setItems] = useState([]);
  const load = () => api.adminBusinesses().then(setItems).catch(() => {});
  useEffect(() => { load(); }, []);
  const toggle = async (id) => {
    try { const r = await api.toggleFeature(id); toast.success(r.featured ? "Destacado activado." : "Destacado desactivado."); load(); }
    catch { toast.error("No se pudo actualizar."); }
  };
  return (
    <div className="overflow-hidden rounded-2xl border border-border bg-card">
      <div className="ml-scroll overflow-x-auto">
        <table className="w-full min-w-[720px] text-sm">
          <thead className="bg-secondary/60 text-left text-xs uppercase tracking-wide text-muted-foreground">
            <tr><th className="p-3">Negocio</th><th className="p-3">Ciudad</th><th className="p-3">Estado</th><th className="p-3">Confianza</th><th className="p-3">Destacar</th></tr>
          </thead>
          <tbody>
            {items.map((b) => (
              <tr key={b.id} data-testid={`admin-biz-${b.id}`} className="border-t border-border">
                <td className="p-3 font-semibold text-brand-ink">{b.name}{b.claimed && <span className="ml-1 text-xs text-emerald-600">✓</span>}</td>
                <td className="p-3 text-muted-foreground">{b.city}, {b.state}</td>
                <td className="p-3"><span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${b.status === "published" ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>{b.status}</span></td>
                <td className="p-3"><ConfidenceBar score={b.confidence_score || 0} size="sm" /></td>
                <td className="p-3">
                  <button data-testid={`feature-toggle-${b.id}`} onClick={() => toggle(b.id)} className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-bold ${b.featured ? "bg-brand text-white" : "border border-border hover:bg-secondary"}`}>
                    <Star className={`h-3.5 w-3.5 ${b.featured ? "fill-white" : ""}`} /> {b.featured ? "Sí" : "No"}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const Skeleton = () => (
  <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
    {Array.from({ length: 8 }).map((_, i) => <div key={i} className="h-20 animate-pulse rounded-2xl bg-secondary/60" />)}
  </div>
);
